<!-- eslint-disable max-lines -->
<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  reactive,
  ref,
  toRaw,
  watch,
  type PropType
} from 'vue'
import LabeledCard from '@/components/inspection/common/LabeledCard.vue'
import {
  INSPECTION_CARD_TYPE,
  type InspectionCardType
} from '@/constants/ui/inspection/common/inspectionCardType'
import { IMAGE_COUNT_LIMIT_FOR_INSPECTION } from '@/constants/accessor/api/common/imageCountLimit'
import * as Step2Type from '@/types/accessor/api/emergencyInspectionResult/assessmentDetails/step2'
import { useInspectionResultStore } from '@/stores/inspectionResultUpsertStore'
import { CONSTRUCTION_TYPE } from '@/constants/accessor/api/emergencyInspectionResult/assessmentInfo/constructionType'
import type { Step2TabType } from '@/types/ui/inspection/tabsTypes'
import { STEP2_TAB } from '@/constants/ui/inspection/step2/tabs'
import { useDisasterStore } from '@/stores/disasterStore'
import { S3_DIRECTORY_NAMES } from '@/constants/accessor/s3/directoryName'
import PhotoDBOperator from '@/accessors/indexedDB/photoDBOperator'
import type { InputSelectOption } from '@/types/ui/inspection/common/inspectionSelectOptions'
import type { InspectionItemObject } from '@/types/accessor/api/emergencyInspectionResult/assessmentDetails/common/inspectionItemObject'
import { RISK_RANK } from '@/constants/accessor/api/emergencyInspectionResult/assessmentDetails/common/riskRank'
import { useSlopeSelected } from '@/composables/inspection/useSlopeSelected'

/** 各調査項目のキーリスト */
export type Step2TypeKeys =
  | keyof Step2Type.AssessmentDetailWoodStep2Neighboring
  | keyof Step2Type.AssessmentDetailWoodStep2Falling
  | keyof Step2Type.AssessmentDetailSteelStep2Neighboring
  | keyof Step2Type.AssessmentDetailSteelStep2Falling
  | keyof Step2Type.AssessmentDetailRcStep2Neighboring
  | keyof Step2Type.AssessmentDetailRcStep2Falling

/** 各調査項目の型 */
export interface InspectionItem {
  pattern: InspectionCardType
  title: string
  inputSelectOption: InputSelectOption[]
  key: Step2TypeKeys
}

/** 建物構造の型 */
export interface BuildingStructureInspectionItem {
  type: string
  items: InspectionItem[]
}

export default defineComponent({
  name: 'InspectionStep2StructureViewer',
  components: {
    LabeledCard
  },
  props: {
    buildingInspectionData: {
      type: Object as PropType<BuildingStructureInspectionItem>,
      required: true
    },
    defaultData: {
      type: String,
      default: '-1'
    },
    imageList: {
      type: Array as PropType<File[]>,
      required: false,
      default: () => []
    },
    inspectionType: {
      type: String as PropType<Step2TabType>,
      required: true
    },
    triggerCheckFlag: {
      type: Boolean,
      default: false
    },
    saveToStoreFlag: {
      type: Boolean,
      default: false
    }
  },
  emit: ['onNeighboringCheckResult', 'onFallingCheckResult', 'savedInspectionResultStore'],
  setup(props, { emit }) {
    const inspectionResultStore = useInspectionResultStore()
    const disasterStore = useDisasterStore()

    /** データセットのテンプレート */
    const datasetTemplate = reactive<
      Partial<
        Record<
          Step2TypeKeys,
          string | InspectionItemObject | Step2Type.SlopeItemObject | Step2Type.PillarItemObject
        >
      >
    >({})
    /** フォーマット済みのデータセット
     * @note キー情報を確定するためにフォーマット
     */
    const formattedDatasets = computed(() => {
      return Object.keys(datasetTemplate).reduce(
        (acc, key) => {
          const step2Key = key as Step2TypeKeys
          acc[step2Key] = datasetTemplate[step2Key]
          return acc
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {} as Record<Step2TypeKeys, any>
      )
    })

    /** 画像リスト保存用のref */
    const imageLists = ref<Record<string, File[]>>({
      surrounding: [],
      differentialSettlement: [],
      basics: [],
      firstFloorSlope: [],
      wall: [],
      corrosionOrTermite: [],
      settlementSlopeSteel: [],
      roofTile: [],
      windowFrame: [],
      exteriorWetMaterial: [],
      exteriorDryMaterial: [],
      signboard: [],
      outdoorStairs: [],
      otherItem: [],
      settlementSlope: [],
      slope: [],
      buckling: [],
      brace: [],
      pillarBeamJoint: [],
      pillarBase: [],
      corrosion: [],
      roofMaterial: [],
      partsDamageGte3: [],
      groundDestructionSettlement: [],
      pillarDamage5: [],
      pillarDamage4: []
    })

    /**
     * 画像ファイルパスリストをもとにIndexedDBから画像を取得する
     * @param imageDirectory 画像保存先ディレクトリ
     * @param imageFilePathList 画像ファイルパスリスト
     */
    const getImageList = async (
      imageDirectory: string,
      imageFilePathList: string[]
    ): Promise<File[]> => {
      const tmpItems = await PhotoDBOperator.getItems(imageDirectory, imageFilePathList)
      return [
        ...tmpItems
          .map(e => {
            if (e === undefined || e.arrayBuffer === undefined) return

            const blob = new Blob([e.arrayBuffer], { type: e.mime })
            const file = new File([blob], e.fileName, { type: e.mime })
            return file
          })
          .filter((file): file is File => file !== undefined)
      ]
    }

    /**
     * storeに保存されているデータをもとに入力値を初期化する（InspectionItemObject用）
     * @param directoryName 画像保存先ディレクトリ名
     * @param storeData storeに保存されているデータ
     */
    const initInspectionItemObject = (
      directoryName: string,
      storeData: InspectionItemObject
    ): InspectionItemObject => ({
      riskRank: storeData.riskRank,
      imageDirectory:
        [
          disasterStore.govCode,
          disasterStore.disasterId,
          inspectionResultStore.commonResultSummary.assessmentId,
          directoryName
        ].join('/') + '/',
      imageFilePathList: [...storeData.imageFilePathList]
    })

    /**
     * storeに保存されているデータをもとに入力値を初期化する（SlopeItemObject用）
     * @param directoryName 画像保存先ディレクトリ名
     * @param storeData storeに保存されているデータ
     */
    const initSlopeItemObject = (
      directoryName: string,
      storeData: Step2Type.SlopeItemObject
    ): Step2Type.SlopeItemObject => {
      const inspectionItemParam = {
        riskRank: storeData.riskRank,
        imageDirectory: storeData.imageDirectory,
        imageFilePathList: storeData.imageFilePathList
      }
      return {
        aboveSlopeFloorLevel: storeData.aboveSlopeFloorLevel,
        ...initInspectionItemObject(directoryName, inspectionItemParam)
      }
    }

    /**
     * storeに保存されているデータをもとに入力値を初期化する（PillarItemObject用）
     * @param directoryName 画像保存先ディレクトリ名
     * @param storeData storeに保存されているデータ
     */
    const initPillarItemObject = (directoryName: string, storeData: Step2Type.PillarItemObject) => {
      const inspectionItemParam = {
        riskRank: storeData.riskRank,
        imageDirectory: storeData.imageDirectory,
        imageFilePathList: storeData.imageFilePathList
      }
      return {
        damagedPillarCount: storeData.damagedPillarCount,
        inspectedPillarCount: storeData.inspectedPillarCount,
        pillarRatio: storeData.pillarRatio,
        ...initInspectionItemObject(directoryName, inspectionItemParam)
      }
    }

    /**
     * データセット作成
     * @param なし
     */
    const createDatasets = () => {
      const constructionType = inspectionResultStore.assessmentInfo.constructionType
      if (
        constructionType === CONSTRUCTION_TYPE.WOOD &&
        props.inspectionType === STEP2_TAB.neighboring
      ) {
        // 木造（隣接建築物・周辺地盤及び構造躯体）
        const tmpDatasets: Step2Type.AssessmentDetailWoodStep2Neighboring = {
          surrounding: initInspectionItemObject(
            S3_DIRECTORY_NAMES.SURROUNDING,
            inspectionResultStore.step2NeighboringWood.surrounding
          ),
          differentialSettlement: initInspectionItemObject(
            S3_DIRECTORY_NAMES.DIFFERENTIAL_SETTLEMENT,
            inspectionResultStore.step2NeighboringWood.differentialSettlement
          ),
          basics: initInspectionItemObject(
            S3_DIRECTORY_NAMES.BASICS,
            inspectionResultStore.step2NeighboringWood.basics
          ),
          firstFloorSlope: initInspectionItemObject(
            S3_DIRECTORY_NAMES.FIRST_FLOOR_SLOPE,
            inspectionResultStore.step2NeighboringWood.firstFloorSlope
          ),
          wall: initInspectionItemObject(
            S3_DIRECTORY_NAMES.WALL,
            inspectionResultStore.step2NeighboringWood.wall
          ),
          corrosionOrTermite: initInspectionItemObject(
            S3_DIRECTORY_NAMES.CORROSION_OR_TERMITE,
            inspectionResultStore.step2NeighboringWood.corrosionOrTermite
          )
        }
        Object.assign(datasetTemplate, tmpDatasets)
      } else if (
        constructionType === CONSTRUCTION_TYPE.WOOD &&
        props.inspectionType === STEP2_TAB.falling
      ) {
        // 木造（落下危険物・転倒危険物）
        const tmpDatasets: Step2Type.AssessmentDetailWoodStep2Falling = {
          roofTile: initInspectionItemObject(
            S3_DIRECTORY_NAMES.ROOF_TILE,
            inspectionResultStore.step2FallingWood.roofTile
          ),
          windowFrame: initInspectionItemObject(
            S3_DIRECTORY_NAMES.WINDOW_FRAME,
            inspectionResultStore.step2FallingWood.windowFrame
          ),
          exteriorWetMaterial: initInspectionItemObject(
            S3_DIRECTORY_NAMES.EXTERIOR_WET_MATERIAL,
            inspectionResultStore.step2FallingWood.exteriorWetMaterial
          ),
          exteriorDryMaterial: initInspectionItemObject(
            S3_DIRECTORY_NAMES.EXTERIOR_DRY_MATERIAL,
            inspectionResultStore.step2FallingWood.exteriorDryMaterial
          ),
          signboard: initInspectionItemObject(
            S3_DIRECTORY_NAMES.SIGNBOARD,
            inspectionResultStore.step2FallingWood.signboard
          ),
          outdoorStairs: initInspectionItemObject(
            S3_DIRECTORY_NAMES.OUTDOOR_STAIRS,
            inspectionResultStore.step2FallingWood.outdoorStairs
          ),
          otherItem: initInspectionItemObject(
            S3_DIRECTORY_NAMES.OTHER_ITEM,
            inspectionResultStore.step2FallingWood.otherItem
          ),
          otherItemName: inspectionResultStore.step2FallingWood.otherItemName
        }
        Object.assign(datasetTemplate, tmpDatasets)
      } else if (
        constructionType === CONSTRUCTION_TYPE.STEEL &&
        props.inspectionType === STEP2_TAB.neighboring
      ) {
        // 鉄骨（隣接建築物・周辺地盤及び構造躯体）
        const tmpDatasets: Step2Type.AssessmentDetailSteelStep2Neighboring = {
          surrounding: initInspectionItemObject(
            S3_DIRECTORY_NAMES.SURROUNDING,
            inspectionResultStore.step2NeighboringSteel.surrounding
          ),
          settlementSlopeSteel: initInspectionItemObject(
            S3_DIRECTORY_NAMES.SETTLEMENT_SLOPE_STEEL,
            inspectionResultStore.step2NeighboringSteel.settlementSlopeSteel
          ),
          slope: initSlopeItemObject(
            S3_DIRECTORY_NAMES.SLOPE,
            inspectionResultStore.step2NeighboringSteel.slope
          ),
          mostDamagedFloor: inspectionResultStore.step2NeighboringSteel.mostDamagedFloor,
          buckling: initInspectionItemObject(
            S3_DIRECTORY_NAMES.BUCKLING,
            inspectionResultStore.step2NeighboringSteel.buckling
          ),
          brace: initInspectionItemObject(
            S3_DIRECTORY_NAMES.BRACE,
            inspectionResultStore.step2NeighboringSteel.brace
          ),
          pillarBeamJoint: initInspectionItemObject(
            S3_DIRECTORY_NAMES.PILLAR_BEAM_JOINT,
            inspectionResultStore.step2NeighboringSteel.pillarBeamJoint
          ),
          pillarBase: initInspectionItemObject(
            S3_DIRECTORY_NAMES.PILLAR_BASE,
            inspectionResultStore.step2NeighboringSteel.pillarBase
          ),
          corrosion: initInspectionItemObject(
            S3_DIRECTORY_NAMES.CORROSION,
            inspectionResultStore.step2NeighboringSteel.corrosion
          )
        }
        Object.assign(datasetTemplate, tmpDatasets)
      } else if (
        constructionType === CONSTRUCTION_TYPE.STEEL &&
        props.inspectionType === STEP2_TAB.falling
      ) {
        // 鉄骨（落下危険物・転倒危険物）
        const tmpDatasets: Step2Type.AssessmentDetailSteelStep2Falling = {
          roofMaterial: initInspectionItemObject(
            S3_DIRECTORY_NAMES.ROOF_MATERIAL,
            inspectionResultStore.step2FallingSteel.roofMaterial
          ),
          windowFrame: initInspectionItemObject(
            S3_DIRECTORY_NAMES.WINDOW_FRAME,
            inspectionResultStore.step2FallingSteel.windowFrame
          ),
          exteriorWetMaterial: initInspectionItemObject(
            S3_DIRECTORY_NAMES.EXTERIOR_WET_MATERIAL,
            inspectionResultStore.step2FallingSteel.exteriorWetMaterial
          ),
          exteriorDryMaterial: initInspectionItemObject(
            S3_DIRECTORY_NAMES.EXTERIOR_DRY_MATERIAL,
            inspectionResultStore.step2FallingSteel.exteriorDryMaterial
          ),
          signboard: initInspectionItemObject(
            S3_DIRECTORY_NAMES.SIGNBOARD,
            inspectionResultStore.step2FallingSteel.signboard
          ),
          outdoorStairs: initInspectionItemObject(
            S3_DIRECTORY_NAMES.OUTDOOR_STAIRS,
            inspectionResultStore.step2FallingSteel.outdoorStairs
          ),
          otherItem: initInspectionItemObject(
            S3_DIRECTORY_NAMES.OTHER_ITEM,
            inspectionResultStore.step2FallingSteel.otherItem
          ),
          otherItemName: inspectionResultStore.step2FallingSteel.otherItemName
        }
        Object.assign(datasetTemplate, tmpDatasets)
      } else if (
        constructionType === CONSTRUCTION_TYPE.RC &&
        props.inspectionType === STEP2_TAB.neighboring
      ) {
        // 鉄筋（隣接建築物・周辺地盤及び構造躯体）
        const tmpDatasets: Step2Type.AssessmentDetailRcStep2Neighboring = {
          partsDamageGte3: initInspectionItemObject(
            S3_DIRECTORY_NAMES.PARTS_DAMAGE_GTE_3,
            inspectionResultStore.step2NeighboringRc.partsDamageGte3
          ),
          surrounding: initInspectionItemObject(
            S3_DIRECTORY_NAMES.SURROUNDING,
            inspectionResultStore.step2NeighboringRc.surrounding
          ),
          groundDestructionSettlement: initInspectionItemObject(
            S3_DIRECTORY_NAMES.GROUND_DESTRUCTION_SETTLEMENT,
            inspectionResultStore.step2NeighboringRc.groundDestructionSettlement
          ),
          settlementSlope: initInspectionItemObject(
            S3_DIRECTORY_NAMES.SETTLEMENT_SLOPE,
            inspectionResultStore.step2NeighboringRc.settlementSlope
          ),
          mostDamagedFloor: inspectionResultStore.step2NeighboringRc.mostDamagedFloor,
          pillarDamage5: initPillarItemObject(
            S3_DIRECTORY_NAMES.PILLAR_DAMAGE_5,
            inspectionResultStore.step2NeighboringRc.pillarDamage5
          ),
          pillarDamage4: initPillarItemObject(
            S3_DIRECTORY_NAMES.PILLAR_DAMAGE_4,
            inspectionResultStore.step2NeighboringRc.pillarDamage4
          )
        }
        Object.assign(datasetTemplate, tmpDatasets)
      } else if (
        constructionType === CONSTRUCTION_TYPE.RC &&
        props.inspectionType === STEP2_TAB.falling
      ) {
        // 鉄筋（落下危険物・転倒危険物）
        const tmpDatasets: Step2Type.AssessmentDetailRcStep2Falling = {
          windowFrame: initInspectionItemObject(
            S3_DIRECTORY_NAMES.WINDOW_FRAME,
            inspectionResultStore.step2FallingRc.windowFrame
          ),
          exteriorWetMaterial: initInspectionItemObject(
            S3_DIRECTORY_NAMES.EXTERIOR_WET_MATERIAL,
            inspectionResultStore.step2FallingRc.exteriorWetMaterial
          ),
          exteriorDryMaterial: initInspectionItemObject(
            S3_DIRECTORY_NAMES.EXTERIOR_DRY_MATERIAL,
            inspectionResultStore.step2FallingRc.exteriorDryMaterial
          ),
          signboard: initInspectionItemObject(
            S3_DIRECTORY_NAMES.SIGNBOARD,
            inspectionResultStore.step2FallingRc.signboard
          ),
          outdoorStairs: initInspectionItemObject(
            S3_DIRECTORY_NAMES.OUTDOOR_STAIRS,
            inspectionResultStore.step2FallingRc.outdoorStairs
          ),
          otherItem: initInspectionItemObject(
            S3_DIRECTORY_NAMES.OTHER_ITEM,
            inspectionResultStore.step2FallingRc.otherItem
          ),
          otherItemName: inspectionResultStore.step2FallingRc.otherItemName
        }
        Object.assign(datasetTemplate, tmpDatasets)
      } else {
        console.error('データセットの生成に失敗しました。')
        return
      }
    }

    /**
     * 画像リストを更新
     * @param なし
     */
    const updateImageLists = async () => {
      await Promise.all(
        (Object.keys(imageLists.value) as Array<Step2TypeKeys>).map(async key => {
          try {
            if (formattedDatasets.value[key] === undefined) return
            imageLists.value[key] = await getImageList(
              formattedDatasets.value[key].imageDirectory,
              formattedDatasets.value[key].imageFilePathList
            )
          } catch (err) {
            console.error('画像リストの初期化に失敗しました。', err)
          }
        })
      )
    }

    /**
     * 画像パスリストを更新
     * @param payload imagePathList: 画像リスト, key: 対象の調査項目を示すキー
     */
    const updateImageFilePathList = (payload: {
      imagePathList: string[]
      key: Step2TypeKeys
    }): void => {
      formattedDatasets.value[payload.key].imageFilePathList = [...payload.imagePathList]
    }

    /**
     * 画像リストを更新
     * LabeledCardのemitで実行させるための関数
     */
    const updateImageList = async () => {
      await updateImageLists()
    }

    /**
     * 入力内容をStoreに保存する
     * @param なし
     * @returns なし
     */
    const saveToStore = (): void => {
      if (inspectionResultStore.assessmentInfo.constructionType === null) return
      if (props.inspectionType === STEP2_TAB.neighboring) {
        inspectionResultStore.setStep2Neighboring<
          typeof inspectionResultStore.assessmentInfo.constructionType
        >(toRaw(formattedDatasets.value))
      } else {
        inspectionResultStore.setStep2Falling<
          typeof inspectionResultStore.assessmentInfo.constructionType
        >(toRaw(formattedDatasets.value))
      }
    }

    /** 入力チェックを行う */
    watch(
      () => props.triggerCheckFlag,
      async newValue => {
        if (newValue) {
          const unselectedList = Object.entries(props.buildingInspectionData.items)
            .filter(([, value]) => {
              return formattedDatasets.value[value.key].riskRank === RISK_RANK.UNSELECTED
            })
            .map(([, value]) => {
              return value.title
            })
          const emitName =
            props.inspectionType === STEP2_TAB.neighboring
              ? 'onNeighboringCheckResult'
              : 'onFallingCheckResult'
          emit(emitName, unselectedList)
        }
      }
    )

    /** Storeへの保存処理を行う */
    watch(
      () => props.saveToStoreFlag,
      newValue => {
        if (!newValue) return
        saveToStore()
        emit('savedInspectionResultStore')
      }
    )

    onMounted(async () => {
      // データセット作成
      createDatasets()
      // 画像リストを更新
      await updateImageLists()
    })

    const { slopeSelected } = useSlopeSelected(formattedDatasets)

    return {
      IMAGE_COUNT_LIMIT_FOR_INSPECTION,
      INSPECTION_CARD_TYPE,
      imageLists,
      formattedDatasets,
      updateImageFilePathList,
      updateImageList,
      slopeSelected
    }
  }
})
</script>

<template>
  <!-- 各カード調査項目 -->
  <div
    v-for="(item, index) in buildingInspectionData.items"
    :key="`${index}-${item.pattern}`"
    class="mb-4"
  >
    <div v-if="formattedDatasets[item.key] && item.key !== 'mostDamagedFloor'">
      <LabeledCard
        :inspectionFieldData="item"
        :targetActionKey="item.key"
        :itemName="item.title"
        :s3DirectoryName="
          formattedDatasets[item.key].imageDirectory.split('/')[
            formattedDatasets[item.key].imageDirectory.split('/').length - 2
          ]
        "
        :imageCount="imageLists[item.key].length"
        :imageCountLimit="IMAGE_COUNT_LIMIT_FOR_INSPECTION"
        :imageList="imageLists[item.key]"
        :imagePathList="formattedDatasets[item.key].imageFilePathList"
        :index="index"
        @updateImagePathList="updateImageFilePathList"
        @updateImageList="updateImageList"
      >
        <template #input-field>
          <b-form-select
            v-if="item.key !== 'slope'"
            v-model="formattedDatasets[item.key].riskRank"
            :options="item.inputSelectOption"
            class="fs-4 shadow"
          />

          <b-form-select
            v-else
            v-model="slopeSelected"
            :options="item.inputSelectOption"
            class="fs-4 shadow"
          />
        </template>

        <!-- TypeA -->
        <template v-if="item.pattern === INSPECTION_CARD_TYPE.TYPE_A" #header-extension>
          <form autocomplete="off" @submit.prevent>
            <b-form-input
              type="text"
              v-model="formattedDatasets['otherItemName']"
              class="bottom-border fs-4"
              placeholder=""
            />
          </form>
        </template>

        <!-- TypeB -->
        <template v-if="item.pattern === INSPECTION_CARD_TYPE.TYPE_B" #extension>
          <div class="d-flex flex-wrap gap-4 mt-4">
            <div class="d-flex items-center gap-2">
              <label class="fs-4">損傷度Ⅴ</label>
              <b-form-input
                type="number"
                v-model="formattedDatasets[item.key].damagedPillarCount"
                class="bottom-border fs-4"
              />
              <span class="fs-4">本</span>
            </div>

            <div class="d-flex items-center gap-2">
              <label class="fs-4">調査柱</label>
              <b-form-input
                type="number"
                v-model="formattedDatasets[item.key].inspectedPillarCount"
                class="bottom-border fs-4"
              />
              <span class="fs-4">本</span>
            </div>

            <div class="d-flex items-center gap-2">
              <label class="fs-4">調査率</label>
              <b-form-input
                type="number"
                v-model="formattedDatasets[item.key].pillarRatio"
                class="bottom-border fs-4"
              />
              <span class="fs-4">%</span>
            </div>
          </div>
        </template>
      </LabeledCard>
    </div>
    <div
      v-else-if="item.key === 'mostDamagedFloor'"
      class="border border-secondary rounded-lg p-4 mb-4 bg-white shadow-sm"
    >
      <div class="d-flex">
        <div class="custom-heading me-4">
          {{ item.title }}
        </div>
        <div>
          <b-form-input
            type="number"
            v-model="formattedDatasets['mostDamagedFloor']"
            class="fs-4"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bottom-border {
  border: none;
  border-bottom: 1px solid black;
  outline: none;
  background-color: transparent;
  color: black;
  width: 50px;
  text-align: center;
}
.custom-heading {
  font-size: 1.75rem;
  font-weight: bold;
}
:deep(.form-select option) {
  font-size: 1.2rem;
}
</style>
